import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../ui/Button';
import { StatCard } from '../ui/StatCard';
import { StatusBanner } from '../shell/StatusBanner';
import { NumericInput } from '../strategy/NumericInput';
import { formatNumber } from '../../utils/format';
import { COLORS } from '../../constants/theme';
import type { ExploreShellProps } from '../../pages/Explore';

/**
 * SF-139: thin wrapper around the existing (already desktop-appropriate) Explore JSX — no layout
 * redesign. Adds a real, focus-managed `<h1>` and replaces the old ad hoc inline war `<div>` with
 * the shared `StatusBanner` primitive; everything else (including desktop's inline
 * submitError/successMessage/warningMessage rendering) is unchanged from pre-SF-139 `Explore.tsx`.
 */
export function DesktopExploreShell({
  acresToExplore,
  isSubmitting,
  submitError,
  successMessage,
  warningMessage,
  handleInputChange,
  handleSubmit,
  maxExplorable,
  kingdomExplorePool,
  isAtWar,
  soldierCostPerAcre,
  goldCostPerAcre,
  availableSoldiers,
  currentlyExploring,
  totalSoldierCost,
  totalGoldCost,
}: ExploreShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  const maxAtLimit = maxExplorable === 0 || acresToExplore === maxExplorable;

  return (
    <div className="space-y-4">
      <h1 ref={h1Ref} tabIndex={-1} className="text-xl font-bold text-game-50 outline-none">
        Explore
      </h1>

      {isAtWar && (
        <StatusBanner
          tone="war"
          icon="⚔️"
          headline="Kingdom at war"
          detail="Exploration costs are doubled."
        />
      )}

      {/* Stat Cards */}
      <div className="flex gap-3">
        <div className="w-36">
          <StatCard
            label="Max Explorable"
            value={`${formatNumber(maxExplorable)} acres`}
            sub="Available now"
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Kingdom Pool"
            value={`${formatNumber(kingdomExplorePool)} acres`}
            sub="Uncharted land"
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Exploring"
            value={`${formatNumber(currentlyExploring)} acres`}
            sub="In transit"
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Soldier Cost"
            value={`${soldierCostPerAcre} / acre`}
            sub={`${formatNumber(availableSoldiers)} available`}
            borderless
          />
        </div>
        <div className="w-40">
          <StatCard
            label="Gold Cost"
            value={`${formatNumber(goldCostPerAcre)}gc / acre`}
            color={COLORS.gold}
            borderless
          />
        </div>
      </div>

      {/* Explore Form + Submit Bar */}
      <div
        className="rounded overflow-hidden"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="p-4" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
          <div className="flex items-center gap-4">
            <span className="text-sm" style={{ color: COLORS.text.secondary }}>Acres to explore</span>
            <NumericInput
              value={acresToExplore}
              max={maxExplorable}
              onChange={handleInputChange}
              className="w-20"
            />
            <button
              onClick={() => handleInputChange(maxExplorable)}
              disabled={maxAtLimit}
              className="text-xs px-2 py-1 rounded"
              style={{
                color: maxAtLimit ? COLORS.text.muted : COLORS.link,
                border: `1px solid ${maxAtLimit ? COLORS.text.muted : COLORS.link}`,
                background: 'none',
                cursor: maxAtLimit ? 'not-allowed' : 'pointer',
                opacity: maxAtLimit ? 0.5 : 1,
              }}
            >
              Max
            </button>
            <span className="text-xs" style={{ color: COLORS.text.tertiary }}>
              of {formatNumber(maxExplorable)} max
            </span>
          </div>
        </div>

        <div className="px-4 py-3 space-y-3">
          {/* Messages */}
          {submitError && (
            <div className="text-sm" style={{ color: COLORS.negative }}>{submitError}</div>
          )}
          {successMessage && (
            <div className="text-sm" style={{ color: COLORS.positive }}>{successMessage}</div>
          )}
          {warningMessage && (
            <div className="text-sm" style={{ color: COLORS.warning }}>{warningMessage}</div>
          )}

          {/* Summary row */}
          <div className="flex items-center gap-6 text-sm">
            <span className="whitespace-nowrap">
              <span style={{ color: COLORS.text.tertiary }}>Exploring: </span>
              <span className="font-mono" style={{ color: COLORS.text.primary }}>
                {formatNumber(acresToExplore)} acres
              </span>
            </span>
            <span className="whitespace-nowrap">
              <span style={{ color: COLORS.text.tertiary }}>Soldier Cost: </span>
              <span className="font-mono" style={{ color: totalSoldierCost > 0 ? COLORS.text.primary : COLORS.text.tertiary }}>
                {formatNumber(totalSoldierCost)} soldiers
              </span>
            </span>
            <span className="whitespace-nowrap">
              <span style={{ color: COLORS.text.tertiary }}>Gold Cost: </span>
              <span className="font-mono" style={{ color: totalGoldCost > 0 ? COLORS.gold : COLORS.text.tertiary }}>
                {formatNumber(totalGoldCost)} gc
              </span>
            </span>
          </div>

          {/* Actions row */}
          <div className="flex items-center">
            <div className="flex gap-2 ml-auto">
              <Button
                onClick={handleSubmit}
                isLoading={isSubmitting}
                disabled={acresToExplore < 1 || acresToExplore > maxExplorable}
              >
                Send Expedition
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
